import { useEffect, useId, useRef } from "react";

/**
 * The camera, reading codes until it finds one.
 *
 * It uses the browser's own `BarcodeDetector` where there is one, so nothing
 * is downloaded to read a code. Where there is not -- Safari on iPhone -- it
 * reads the frames itself with a bundled decoder (`lib/scanDecoder.ts`),
 * fetched only then. Mounted, it asks for the camera and looks four times a
 * second; the first code it reads goes to `onRead` and it stops looking.
 * Unmounted, the camera is released.
 *
 * Shared by the Scan screen, which looks the code up, the search box, which
 * searches for it, and `ScanSerialDialog`, which puts a serial number in a box
 * after a person has checked it.
 *
 * Over the picture is a frame to aim with: a squarish window for a QR label,
 * a wide strip for a serial's barcode (`aim`), the rest dimmed. It is only a
 * guide -- the whole frame is read -- but a code filling the frame, square to
 * the camera, is what reads in one look instead of twenty.
 *
 * Built for Safari on iPhone, which got the first version wrong twice: a
 * video sized by a height limit is not stretched to fill (`object-fit` is
 * ignored), so it showed with bars at the sides; and a huge box-shadow used
 * as the dimming drew as a stray triangle. Now the window has a fixed height,
 * the video is pinned to all four edges of it, and the dimming is an SVG
 * mask with a hole cut where the frame is, sized in percentages so the two
 * always line up. The camera is asked for a sharper picture
 * than its default 640x480, which is too coarse for the narrow bars of a
 * serial number on a laptop box.
 */

interface Detector {
  detect(source: HTMLVideoElement): Promise<{ rawValue: string }[]>;
}
interface DetectorConstructor {
  new (options?: { formats?: string[] }): Detector;
  getSupportedFormats?: () => Promise<string[]>;
}

const detectorConstructor = (): DetectorConstructor | undefined =>
  (window as unknown as { BarcodeDetector?: DetectorConstructor }).BarcodeDetector;

const FORMATS = ["qr_code", "code_128", "code_39", "ean_13", "ean_8", "upc_a", "data_matrix"];

/** Reads one frame of the video: the code's text, or null. */
type FrameRead = (source: HTMLVideoElement) => Promise<string | null>;

/** Bigger frames read no better and cost a phone a lot more per look. */
const MAX_FRAME_EDGE = 1024;

async function frameReader(): Promise<FrameRead> {
  const Native = detectorConstructor();
  if (Native) {
    const supported = (await Native.getSupportedFormats?.()) ?? [];
    const detector = new Native({
      formats: supported.length ? FORMATS.filter((format) => supported.includes(format)) : FORMATS,
    });
    return async (source) => (await detector.detect(source))[0]?.rawValue || null;
  }
  const { makeFrameReader } = await import("../lib/scanDecoder.js");
  const read = makeFrameReader();
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d", { willReadFrequently: true });
  return async (source) => {
    if (!context || !source.videoWidth) return null;
    const scale = Math.min(1, MAX_FRAME_EDGE / Math.max(source.videoWidth, source.videoHeight));
    canvas.width = Math.round(source.videoWidth * scale);
    canvas.height = Math.round(source.videoHeight * scale);
    context.drawImage(source, 0, 0, canvas.width, canvas.height);
    return read(context.getImageData(0, 0, canvas.width, canvas.height));
  };
}

/**
 * The camera is only "unsupported" where there is no camera API at all. It
 * used to be whenever `BarcodeDetector` was missing, which on an iPhone meant
 * the screen gave up before asking for the camera, so no prompt ever came.
 */
/** Where the window is, as a share of the view, for both the frame and the hole in the dimming. */
const AIM = {
  code: { x: "20%", y: "12%", width: "60%", height: "76%" },
  barcode: { x: "6%", y: "33%", width: "88%", height: "34%" },
} as const;

export const hasCamera = (): boolean => typeof navigator.mediaDevices?.getUserMedia === "function";

export function CameraView({
  onRead,
  onFail,
  aim = "code",
}: {
  onRead: (text: string) => void;
  onFail: () => void;
  /** "code" for a QR label or anything, "barcode" for a long one-line barcode. */
  aim?: "code" | "barcode";
}) {
  const video = useRef<HTMLVideoElement>(null);
  // The loop outlives a render, so it reads the latest handlers from here.
  const handlers = useRef({ onRead, onFail });
  handlers.current = { onRead, onFail };

  useEffect(() => {
    let stopped = false;
    let stream: MediaStream | null = null;
    let timer = 0;

    async function start() {
      try {
        // Asked for first, so the permission prompt is not waiting on a download.
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment", width: { ideal: 1920 }, height: { ideal: 1080 } },
        });
        if (stopped || !video.current) return;
        video.current.srcObject = stream;
        await video.current.play();
        const read = await frameReader();

        const look = async () => {
          if (stopped || !video.current) return;
          try {
            const found = await read(video.current);
            if (found && !stopped) {
              handlers.current.onRead(found);
              return;
            }
          } catch {
            // A frame that could not be read is just a frame; the next one may be fine.
          }
          timer = window.setTimeout(() => void look(), 250);
        };
        void look();
      } catch {
        if (!stopped) handlers.current.onFail();
      }
    }
    void start();

    return () => {
      stopped = true;
      window.clearTimeout(timer);
      stream?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  const mask = useId();
  const hole = AIM[aim];

  return (
    <div className="scan-view">
      <video ref={video} className="scan-video" playsInline muted aria-label="Camera view" />
      <svg className="scan-shade" aria-hidden="true">
        <defs>
          <mask id={mask}>
            <rect width="100%" height="100%" fill="white" />
            <rect x={hole.x} y={hole.y} width={hole.width} height={hole.height} rx="12" fill="black" />
          </mask>
        </defs>
        <rect className="scan-shade-fill" width="100%" height="100%" mask={`url(#${mask})`} />
      </svg>
      <div
        className="scan-aim"
        style={{ left: hole.x, top: hole.y, width: hole.width, height: hole.height }}
        aria-hidden="true"
      >
        <span className="scan-aim-hint">
          {aim === "barcode" ? "Fit the barcode inside the box" : "Fit the code inside the box"}
        </span>
      </div>
    </div>
  );
}
